/**
 * Bundle Gift Guard
 *
 * Keeps the free "IMUN 5" gift line in sync with the cart's actual
 * contents, even when items are added/removed from the cart page or the
 * cart drawer - i.e. from anywhere OUTSIDE the Build Your Bundle section
 * (sections/build-your-bundle.liquid), which only runs on the bundle page
 * itself and has no idea what happens to the cart afterwards.
 *
 * Eligibility is based ONLY on line items carrying the
 * '_bundle': 'Bundle of 24' property, which build-your-bundle.liquid
 * already stamps on every product it adds. A plain purchase of 24 bottles
 * assembled outside that section (e.g. straight from a product page) never
 * carries this property, so it can never trigger the gift - on purpose.
 *
 * Runs site-wide (included from layout/theme.liquid) because cart changes
 * can happen from the cart drawer on any page, not just on /cart.
 *
 * Debounced + always re-fetches a fresh /cart.js snapshot right before
 * deciding anything, instead of trusting whatever cart snapshot a given
 * event happened to carry.
 *
 * Does NOT rely solely on the theme's cart-update event. This store also
 * runs the Samita Order Limits app, which hooks into the same event bus
 * (and many others) to manage the cart itself - there is no guarantee the
 * event this script listens for always fires cleanly through that layer.
 * So on top of listening for it as a fast path, this also polls /cart.js
 * on a fixed interval as a safety net: whatever changed the cart, and
 * however it did it, the gift line is corrected within a couple of
 * seconds regardless.
 */
(function () {
  var BUNDLE_TAG_VALUE = 'Bundle of 24';
  var GIFT_PROPERTY = '_bundle_gift';
  var BUNDLE_SIZE = 24;
  var DEBOUNCE_MS = 400;

  // Gift product variant (IMUN 5 - 100% Organic, handle "imun5", the same
  // one referenced by the Gift Product setting on the Build Your Bundle
  // section). Used only to re-add the gift line if it ever goes missing
  // while the cart still qualifies (e.g. bundle-tagged quantity increased
  // back up via the cart page's own quantity stepper).
  var GIFT_VARIANT_ID = 63197436346717;

  // NOTE: window.routes.cart_url is Shopify's cart PAGE url ('/cart'),
  // not the JSON API - fetching it here returned an HTML document, which
  // is exactly the "Unexpected token '<'" failure seen while debugging
  // this. The JSON cart endpoint is always '/cart.js', unconditionally.
  var CART_URL = '/cart.js';
  var UPDATE_URL = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  var ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

  var debounceTimer = null;
  var applying = false; // true while a correction fetch is in flight
  var recheckNeeded = false; // a new trigger arrived while one was in flight

  function scheduleCheck() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runCheck, DEBOUNCE_MS);
  }

  function runCheck() {
    if (applying) {
      // A correction is already in flight - re-run once it settles so we
      // always finish on the freshest possible state instead of guessing.
      recheckNeeded = true;
      return;
    }
    fetch(CART_URL)
      .then(function (response) {
        return response.json();
      })
      .then(applyCorrection)
      .catch(function () {});
  }

  function maybeRecheck() {
    if (recheckNeeded) {
      recheckNeeded = false;
      scheduleCheck();
    }
  }

  function applyCorrection(cart) {
    if (!cart || !cart.items) return;

    var bundleQty = 0;
    var giftLines = []; // handle any leftover duplicate gift lines too

    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props[GIFT_PROPERTY] === 'true') {
        // The gift line itself also carries '_bundle': 'Bundle of 24'
        // (build-your-bundle.liquid stamps both properties on it), so it
        // must be excluded here - otherwise its own presence counts
        // towards the threshold that justifies its own presence, which
        // keeps it "stuck" whenever real bottle count is exactly one
        // short of a clean multiple of 24 (e.g. 23 real bottles + the
        // gift itself = 24, so the check never sees it as short).
        giftLines.push(item);
        return;
      }
      if (props['_bundle'] === BUNDLE_TAG_VALUE) {
        bundleQty += item.quantity;
      }
    });

    var desiredGiftQty = Math.floor(bundleQty / BUNDLE_SIZE);
    var currentGiftQty = giftLines.reduce(function (sum, l) { return sum + l.quantity; }, 0);

    if (currentGiftQty === desiredGiftQty && giftLines.length <= 1) {
      maybeRecheck();
      return; // already correct, and nothing duplicated to clean up
    }
    if (giftLines.length === 0 && desiredGiftQty === 0) {
      maybeRecheck();
      return; // nothing to do and nothing to remove
    }

    applying = true;

    if (giftLines.length === 0) {
      // Bundle-tagged quantity is >=24 but there is no gift line - re-add
      // it, mirroring exactly what build-your-bundle.liquid itself sends.
      fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: GIFT_VARIANT_ID,
            quantity: desiredGiftQty,
            properties: {
              '_bundle': BUNDLE_TAG_VALUE,
              '_bundle_gift': 'true',
              '_product_name': 'IMUN 5 – 100% Organic (CADOU)'
            }
          }]
        })
      })
        .then(function (response) { return response.json(); })
        .then(afterCorrection)
        .catch(onCorrectionError);
      return;
    }

    // One or more gift lines exist: keep the first at the desired
    // quantity and zero out any duplicates, all in a single request.
    var updates = {};
    updates[giftLines[0].key] = desiredGiftQty;
    for (var i = 1; i < giftLines.length; i++) {
      updates[giftLines[i].key] = 0;
    }

    fetch(UPDATE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (response) { return response.json(); })
      .then(afterCorrection)
      .catch(onCorrectionError);
  }

  function afterCorrection(newCart) {
    applying = false;

    // A correction removes/re-adds a line, which shifts the numeric
    // position of every line after it. The theme's own cart page/drawer
    // buttons are wired to that position (not to a stable key), so if the
    // shopper clicks one before the page catches up, Shopify rejects it
    // ("invalid line parameter"). A background partial re-render (via the
    // theme's cart-update event) doesn't close that window reliably, so
    // force a full reload of the cart page itself - the one place a
    // shopper would actually see/interact with this - to guarantee the
    // positions they see always match what the server has. Leave the
    // bundle builder page alone: it doesn't use position-based line
    // actions, so there's nothing to desync there, and a reload would
    // just interrupt an in-progress build.
    var path = window.location.pathname;
    var onCartPage = path === '/cart' || path.indexOf('/cart/') === 0;
    if (onCartPage) {
      window.location.reload();
      return;
    }

    // Elsewhere (e.g. the cart drawer open on some other page), let the
    // theme's own cart UI know it should re-render its own markup.
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-gift-guard', cartData: newCart });
    }

    maybeRecheck();
  }

  function onCorrectionError() {
    applying = false;
    maybeRecheck();
  }

  // Fast path: react immediately when the theme's own cart-update event
  // fires (works most of the time, but see the note above on why it's
  // not the only mechanism here).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'bundle-gift-guard') return; // ignore our own correction
      scheduleCheck();
    });
  }

  // Safety net: poll on a fixed interval regardless of any event system,
  // so the gift line self-corrects even if nothing above ever fires.
  var POLL_MS = 2000;
  setInterval(scheduleCheck, POLL_MS);

  document.addEventListener('DOMContentLoaded', scheduleCheck);
})();